import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/shared/auth';
import type { Maybe } from '@/shared/libs';
import { Button, LoaderOverlay, PageHeader } from '@/shared/ui';
import { resolveUserRole, UserRoleEnum } from '@entities/User';
import type { Offer } from '@psp-aggregator/sdk';
import { AppShell, markCatalogSeen, useNavBadges } from '@widgets/AppShell';
import { DealTicket } from '@widgets/DealTicket';
import { MerchantApply } from '@widgets/MerchantApply';
import { MerchantCatalog } from '@widgets/MerchantCatalog';
import { MerchantRequests } from '@widgets/MerchantRequests';

import {
  isMerchantTab,
  MERCHANT_TABS,
  MerchantTab,
} from '../../model/merchant-tabs';

const MERCHANT_HEADING_KEY: Record<
  MerchantTab,
  'pages.merchant.headings.catalog' | 'pages.merchant.headings.applications'
> = {
  [MerchantTab.Applications]: 'pages.merchant.headings.applications',
  [MerchantTab.Catalog]: 'pages.merchant.headings.catalog',
};

const MERCHANT_SUBTITLE_KEY: Record<
  MerchantTab,
  'pages.merchant.subtitles.catalog' | 'pages.merchant.subtitles.applications'
> = {
  [MerchantTab.Applications]: 'pages.merchant.subtitles.applications',
  [MerchantTab.Catalog]: 'pages.merchant.subtitles.catalog',
};

export interface MerchantDashboardViewProps {
  activeTab: MerchantTab;
  onTabChange: (tab: MerchantTab) => void;
}

export const MerchantDashboardView: FC<MerchantDashboardViewProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedOffers, setSelectedOffers] = useState<Offer[]>([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<Maybe<string>>(null);
  const role = user ? resolveUserRole(user.role) : undefined;
  const badges = useNavBadges(role);

  useEffect(() => {
    if (activeTab === MerchantTab.Catalog) {
      markCatalogSeen(role ?? UserRoleEnum.Merchant);
    }
  }, [activeTab, role]);

  const tabs = useMemo(
    () =>
      MERCHANT_TABS.map((tab) => ({
        badge:
          tab.key === MerchantTab.Applications
            ? badges.deals || undefined
            : tab.key === MerchantTab.Catalog
              ? badges.catalog || undefined
              : undefined,
        badgeTone: (tab.key === MerchantTab.Applications ? 'rose' : 'amber') as
          'rose' | 'amber',
        icon: tab.icon,
        key: tab.key,
        label: t(tab.labelKey),
      })),
    [badges.catalog, badges.deals, t],
  );

  const handleTabChange = (tab: string): void => {
    if (isMerchantTab(tab)) {
      onTabChange(tab);
    }
  };

  if (!user) {
    return <LoaderOverlay show />;
  }

  const tabKey = isMerchantTab(activeTab) ? activeTab : MerchantTab.Catalog;
  const content: Record<MerchantTab, ReactNode> = {
    [MerchantTab.Applications]: (
      <MerchantRequests onOpenDeal={setSelectedDealId} />
    ),
    [MerchantTab.Catalog]: (
      <MerchantCatalog
        selectedIds={selectedOffers.map((offer) => offer.id)}
        onSelectionChange={setSelectedOffers}
      />
    ),
  };

  return (
    <AppShell activeTab={activeTab} tabs={tabs} onTabChange={handleTabChange}>
      <div>
        <PageHeader
          action={
            tabKey === MerchantTab.Catalog ? (
              <Button
                disabled={selectedOffers.length === 0}
                size="sm"
                type="button"
                variant="brand"
                onClick={() => setApplyOpen(true)}>
                {t('pages.merchant.catalog.apply')} ({selectedOffers.length})
              </Button>
            ) : undefined
          }
          subtitle={t(MERCHANT_SUBTITLE_KEY[tabKey])}
          title={t(MERCHANT_HEADING_KEY[tabKey])}
        />

        <div className="mt-6">{content[tabKey]}</div>
      </div>
      {applyOpen && (
        <MerchantApply
          offers={selectedOffers}
          onClose={() => setApplyOpen(false)}
          onCreated={() => setSelectedOffers([])}
        />
      )}
      {!!selectedDealId && (
        <DealTicket
          dealId={selectedDealId}
          onClose={() => setSelectedDealId(null)}
        />
      )}
    </AppShell>
  );
};

MerchantDashboardView.displayName = 'MerchantDashboardView';
